import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchApi,
  GCacheQuerySectionProps,
  makeResetCache,
  PaginatorAndListSettings,
  ParallelLoad,
  selectUser,
  UneditableReference,
  useCacheQuery,
  UserState,
} from 'gossamer-front-end';
import { ConvoUserView, ConvoUserViewRow } from 'cb-model';
import { displayDateTimeToMinute, getRowKey, QueryOptions } from 'gossamer-unified';
import styles from './InboxPage.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { getConvoContextInfo, getRelevantUserId } from 'cb-unified';
import { CreateUserConvoModal } from './CreateUserConvoModal';
import { Button } from 'react-bootstrap';

interface LeftPaneProps {
  activeConvoId: number | null;
  closePane: () => void;
}

const getInitialConvoListSize = () => Math.ceil((window.innerHeight - 200) / 60);

export const LeftPane = (props: LeftPaneProps) => {
  const user = useSelector((state: { user: UserState }) => selectUser(state));
  const dispatch = useDispatch();
  const [isMarkingAllRead, setIsMarkingAllRead] = React.useState(false);
  const [convoQueryOptions, setConvoQueryOptions] = React.useState({
    where: { and: [] },
    order: [['latest_message_at', 'DESC']],
    limit: getInitialConvoListSize(),
  } as QueryOptions);
  const convoQuery = useCacheQuery(ConvoUserView, convoQueryOptions);

  const handleMarkAllRead = async () => {
    setIsMarkingAllRead(true);
    try {
      await fetchApi('POST', '/main/mark-all-convos-read', {});
      const resetCache = makeResetCache(dispatch);
      resetCache(ConvoUserView.id);
      if (props.activeConvoId) {
        resetCache(ConvoUserView.id, String(props.activeConvoId));
      }
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  return (
    <>
      <div className={styles.LeftPaneActions}>
        {!!user && user.roleId !== 'user' && (
          <div>
            <CreateUserConvoModal />
          </div>
        )}
        <Button variant="outline-secondary" size="sm" onClick={handleMarkAllRead} disabled={isMarkingAllRead}>
          Mark all read
        </Button>
      </div>
      <PaginatorAndListSettings
        entity={ConvoUserView}
        gCacheQuery={convoQuery}
        queryOptions={convoQueryOptions}
        render={(innerProps) => (
          <ConvoList {...innerProps} activeConvoId={props.activeConvoId} closePane={props.closePane} />
        )}
        setQueryOptions={setConvoQueryOptions}
        showSettingsControl={false}
        showItemCount={false}
      />
    </>
  );
};

type ConvoListProps = GCacheQuerySectionProps<ConvoUserViewRow> & {
  activeConvoId: number | null;
  closePane: () => void;
};

const ConvoList = (props: ConvoListProps) => {
  const user = useSelector((state: { user: UserState }) => selectUser(state));
  return (
    <ParallelLoad responses={[props.gCacheQuery]}>
      <div className={styles.ConvoList}>
        {!!user &&
          (props.gCacheQuery.data || []).map((row) => (
            <ConvoTile
              key={getRowKey(ConvoUserView, row)}
              row={row}
              isActive={row.id === props.activeConvoId}
              currentUserId={user.userId}
              closePane={props.closePane}
            />
          ))}
      </div>
    </ParallelLoad>
  );
};

type ConvoTileProps = {
  isActive: boolean;
  row: ConvoUserViewRow;
  closePane: () => void;
  currentUserId: string;
};

export const ConvoTile = (props: ConvoTileProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const basepath = location.pathname.substring(0, location.pathname.lastIndexOf('/inbox')) + '/inbox';
  const classes = [styles.ConvoTile];
  if (props.isActive) {
    classes.push(styles.Current);
  }
  const unreadMark = props.row.latest_message_id > props.row.latest_read_message_id ? '•' : '';
  const contextInfo = getConvoContextInfo(
    props.row.forum_type,
    props.row.forum_key,
    getRelevantUserId(props.row.created_by, props.row.recipient_user_id, props.currentUserId),
  );
  return (
    <div
      className={classes.join(' ')}
      key={props.row.id}
      onClick={() => {
        props.closePane();
        navigate(`${basepath}/${props.row.id}`);
      }}
    >
      <div className={styles.ConvoTileUnread}>{unreadMark}</div>
      <div className={styles.ConvoTileMain}>
        <div className={styles.ConvoSubject}>
          <img src={contextInfo.icon} />
          <UneditableReference refEntity={contextInfo.entity} value={contextInfo.key} suppressLink={true} />
        </div>
        <div className={styles.ConvoSubHeading}>{props.row.subject_text}</div>
      </div>
      <div className={styles.ConvoTileDateTime}>
        <div className={styles.VeryMuted}>{displayDateTimeToMinute(props.row.latest_message_at).replace(',', '')}</div>
      </div>
    </div>
  );
};
